import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Hourglass, CheckCircle2, X, FileText, Eye, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const FILTERS = [
  { id: "pending", label: "À valider", color: "bg-sun-100 text-sun-700" },
  { id: "verified", label: "Validés", color: "bg-mint-100 text-mint-700" },
  { id: "rejected", label: "Refusés", color: "bg-pink-100 text-pink-600" },
];

export default function AdminResidences() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/residence-verifications?status_filter=${filter}`)
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const decide = async (userId, status, reasonText = "") => {
    setBusy(userId);
    try {
      await api.patch(`/admin/residence-verifications/${userId}`, { status, reason: reasonText });
      toast.success(status === "verified" ? "Justificatif validé" : "Justificatif refusé");
      setRejectFor(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const counts = items.length;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="phenix-eyebrow mb-2 inline-flex items-center gap-1.5">
            <ShieldCheck size={11} /> Modération
          </div>
          <h1 className="heading-display text-[1.8rem] sm:text-[2.2rem] text-plum-900">Justificatifs de domicile</h1>
          <p className="text-[13px] text-plum-700/65 mt-1">Validez les justificatifs pour ouvrir le co-voiturage aux Istréens.</p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-full h-10 px-4 inline-flex items-center gap-1.5">
          <RefreshCw size={13} /> Rafraîchir
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto mb-5" data-testid="residence-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`residence-filter-${f.id}`}
            className={`shrink-0 h-10 px-4 rounded-full text-[13px] font-bold transition-all ${
              filter === f.id ? `${f.color} shadow-sm` : "bg-white text-plum-700 border border-cream-200 hover:bg-cream-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="phenix-card p-10 animate-pulse h-32" />
      ) : counts === 0 ? (
        <div className="phenix-card p-10 text-center bg-cream-50/40">
          <FileText size={28} className="mx-auto text-plum-700/30 mb-2" />
          <h3 className="font-display font-bold text-plum-900">
            {filter === "pending" ? "Aucun justificatif en attente" : filter === "verified" ? "Aucun habitant vérifié pour l'instant" : "Aucun refus enregistré"}
          </h3>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="residence-list">
          {items.map((u) => (
            <li key={u.user_id} className="phenix-card bg-white p-4 sm:p-5" data-testid={`residence-row-${u.user_id}`}>
              <div className="flex flex-wrap items-start gap-4">
                <button
                  onClick={() => setPreview(u)}
                  className="shrink-0 w-20 h-24 rounded-xl border border-cream-200 overflow-hidden bg-cream-50 grid place-items-center relative group"
                  data-testid={`residence-preview-${u.user_id}`}
                >
                  {u.residence_doc_url?.startsWith("data:image") ? (
                    <img src={u.residence_doc_url} alt="justificatif" className="w-full h-full object-cover" />
                  ) : (
                    <FileText size={26} className="text-plum-700/40" />
                  )}
                  <span className="absolute inset-0 bg-plum-900/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center text-white">
                    <Eye size={20} />
                  </span>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-display font-extrabold text-plum-900 text-base">{u.pseudo || u.name}</h3>
                    <span className="phenix-pill bg-cream-100 text-plum-700/75 text-[10px] uppercase">{u.role}</span>
                    {u.residence_status === "pending" && <span className="phenix-pill bg-sun-100 text-sun-700 text-[10px] inline-flex items-center gap-1"><Hourglass size={10} /> En attente</span>}
                    {u.residence_status === "verified" && <span className="phenix-pill bg-mint-100 text-mint-700 text-[10px] inline-flex items-center gap-1"><ShieldCheck size={10} /> Vérifié</span>}
                    {u.residence_status === "rejected" && <span className="phenix-pill bg-pink-100 text-pink-600 text-[10px] inline-flex items-center gap-1"><ShieldAlert size={10} /> Refusé</span>}
                  </div>
                  <p className="text-[12px] text-plum-700/65">{u.email}</p>
                  <p className="text-[11px] text-plum-700/45 mt-0.5">
                    Déposé {u.residence_doc_uploaded_at ? new Date(u.residence_doc_uploaded_at).toLocaleString("fr-FR") : "—"}
                  </p>
                  {u.residence_rejected_reason && (
                    <p className="text-[11px] text-pink-600 mt-1 italic">Motif refus : {u.residence_rejected_reason}</p>
                  )}
                  {u.residence_status === "pending" && (
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => decide(u.user_id, "verified")}
                        disabled={busy === u.user_id}
                        className="rounded-full bg-mint-600 hover:bg-mint-700 text-white h-10 px-4 text-[12px] font-bold inline-flex items-center gap-1.5"
                        data-testid={`approve-${u.user_id}`}
                      >
                        <CheckCircle2 size={13} /> Valider
                      </Button>
                      <Button
                        onClick={() => { setRejectFor(u); setReason(""); }}
                        disabled={busy === u.user_id}
                        variant="outline"
                        className="rounded-full h-10 px-4 text-[12px] font-bold inline-flex items-center gap-1.5"
                        data-testid={`reject-${u.user_id}`}
                      >
                        <X size={13} /> Refuser
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="residence-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle>{preview.pseudo || preview.name} — Justificatif</DialogTitle>
              </DialogHeader>
              <div className="bg-cream-50 rounded-xl p-2">
                {preview.residence_doc_url?.startsWith("data:image") ? (
                  <img src={preview.residence_doc_url} alt="justificatif" className="w-full max-h-[70vh] object-contain rounded-lg" />
                ) : preview.residence_doc_url?.startsWith("data:application/pdf") ? (
                  <embed src={preview.residence_doc_url} type="application/pdf" className="w-full h-[70vh] rounded-lg" />
                ) : (
                  <p className="p-6 text-center text-plum-700/65">Aperçu indisponible.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          {rejectFor && (
            <>
              <DialogHeader>
                <DialogTitle>Refuser le justificatif</DialogTitle>
              </DialogHeader>
              <p className="text-[13px] text-plum-700/75 mb-3">
                Précisez le motif. Il sera communiqué à <strong>{rejectFor.pseudo || rejectFor.name}</strong>.
              </p>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Ex : Document illisible, hors-Istres, expiré…"
                className="rounded-xl bg-cream-50 mb-3"
                data-testid="reject-reason"
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => decide(rejectFor.user_id, "rejected", reason)}
                  disabled={busy === rejectFor.user_id || !reason.trim()}
                  className="flex-1 rounded-full bg-pink-500 hover:bg-pink-600 text-white h-11 font-bold"
                  data-testid="confirm-reject"
                >
                  Confirmer le refus
                </Button>
                <Button variant="outline" onClick={() => setRejectFor(null)} className="rounded-full h-11 px-5">
                  Annuler
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
